import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {VulnerabilityOptionLists} from '../../features/vulnerabilities/useVulnerabilityOptions';

/** A row in the "New Vulnerabilities" table; empty strings mean not chosen yet. */
export type NewVulnerabilityRow = {
  id: string;
  vulnerabilityType: string;
  serviceLevel: string;
};

export const isNewRowComplete = (row: NewVulnerabilityRow): boolean =>
  Boolean(row.vulnerabilityType && row.serviceLevel);

type Props = {
  rows: NewVulnerabilityRow[];
  options: VulnerabilityOptionLists;
  onAdd: () => void;
  onChange: (rowId: string, patch: Partial<NewVulnerabilityRow>) => void;
  onRemove: (rowId: string) => void;
  readOnly?: boolean;
};

/**
 * SFP tube stop: vulnerabilities added in this review. They are created in
 * Curo on review submission, so a row can be removed freely until then.
 */
export const NewVulnerabilitiesTable: React.FC<Props> = ({
  rows,
  options,
  onAdd,
  onChange,
  onRemove,
  readOnly = false,
}) => {
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          New Vulnerabilities
        </Typography>
        {readOnly ? null : (
          <IconButton
            size="small"
            color="primary"
            aria-label="Add vulnerability"
            onClick={onAdd}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {rows.length === 0 ? (
        <Typography color="text.secondary">No new vulnerabilities</Typography>
      ) : (
        <Table size="small" aria-label="New vulnerabilities">
          <TableHead>
            <TableRow>
              <TableCell>Vulnerability</TableCell>
              <TableCell>Service Level</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{minWidth: 200}}>
                  <TextField
                    select
                    SelectProps={{native: true}}
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    inputProps={{'aria-label': 'Vulnerability type'}}
                    value={row.vulnerabilityType}
                    error={!row.vulnerabilityType}
                    onChange={(event) =>
                      onChange(row.id, {vulnerabilityType: event.target.value})
                    }
                  >
                    <option value="">Select a vulnerability</option>
                    {options.vulnerabilityTypes.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{minWidth: 320}}>
                  <TextField
                    select
                    SelectProps={{native: true}}
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    inputProps={{'aria-label': 'Service level'}}
                    value={row.serviceLevel}
                    error={!row.serviceLevel}
                    onChange={(event) =>
                      onChange(row.id, {serviceLevel: event.target.value})
                    }
                  >
                    <option value="">Select a service level</option>
                    {options.serviceLevels.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </TextField>
                  {isNewRowComplete(row) ? null : (
                    <Typography variant="caption" color="error">
                      Choose a vulnerability type and a service level
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {readOnly ? null : (
                    <IconButton
                      size="small"
                      aria-label="Remove vulnerability"
                      onClick={() => onRemove(row.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
